import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Loader2, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, LOGO_URL } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin/dashboard");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[var(--brand-ink)] px-6">
      <div className="grain absolute inset-0" />
      <div className="relative w-full max-w-md" data-testid="admin-login">
        <Link
          to="/"
          data-testid="admin-back-to-site"
          className="mb-6 inline-flex items-center gap-2 text-sm text-white/60 transition-colors hover:text-white"
        >
          <ArrowLeft size={15} /> Back to site
        </Link>
        <div className="mb-8 flex items-center gap-3">
          <img src={LOGO_URL} alt="NLCS logo" className="h-12 w-12 object-contain" />
          <div>
            <div className="font-display text-2xl font-extrabold text-white">NLCS</div>
            <div className="text-xs uppercase tracking-[0.2em] text-white/50">Admin Portal</div>
          </div>
        </div>

        <form onSubmit={submit} className="border border-white/10 bg-white p-8">
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold">
            <Lock size={20} className="text-[var(--brand-primary)]" /> Sign in
          </h1>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Restricted area — authorised staff only.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="admin-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  data-testid="admin-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  data-testid="toggle-admin-password"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--text-secondary)] transition-colors hover:text-[var(--text-primary)]"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <p className="mt-4 border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="login-error">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="mt-6 flex w-full items-center justify-center gap-2 bg-[var(--brand-primary)] py-3.5 font-semibold text-white transition-colors hover:bg-[var(--brand-primary-hover)] disabled:opacity-50"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
